import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { MenuItem } from '@atlassian/universal-create/types';

import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useCanCreateSpace } from '@confluence/create-space/entry-points/createSpace.ts';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { fg } from '@confluence/feature-gating';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useCfind5291TemplateTouchpoint } from '@confluence/experiment-cfind-5291-template-touchpoint/entry-points/useCfind5291TemplateTouchpoint.ts';
import { expValEquals, expVal } from '@confluence/feature-experiments';

import { useSpaceEnabledContentTypes } from '../../CreateContentPopover/useSpaceEnabledContentTypes.tsx';
import { useIsCreateMenuLoomRecorderEntryPointEnabled } from '../../CreateContentPopover/CreateContentPopoverItems/index.ts';
import { useIsFreeOrStandardUser } from '../useIsFreeOrStandardUser.ts';
import { useLiveDocItem } from './useLiveDocItem.tsx';
import { usePageItem } from './usePageItem.tsx';
import { useWhiteboardItem } from './useWhiteboardItem.tsx';
import { useDatabaseItem } from './useDatabaseItem.tsx';
import { useBlogItem } from './useBlogItem.tsx';
import { useEmbedItem } from './useEmbedItem.tsx';
import { useSpaceItem } from './useSpaceItem.tsx';
import { useLoomItem } from './useLoomItem.tsx';
import { useAgentItem } from './useAgentItem.tsx';
import { useAISmartCreateItem } from './useAISmartCreateItem.tsx';
import { useSlideItem } from './useSlideItem.tsx';
import { useStartFromTemplateItem } from './useStartFromTemplateItem.tsx';

export const ANALYTICS_EVENT_SOURCE = 'globalCreateContentPopover';
export const OVERFLOW_THRESHOLD_TEMPLATE_TOUCHPOINT = 5;

const messages = defineMessages({
	// eslint-disable-next-line @atlassian/i18n/require-description
	moreFromAtlassian: {
		id: 'create-blank-fabric-page.universal-create.suggestions',
		defaultMessage: 'Suggestions',
	},
});

export type UseMenuItemProps = {
	parentId?: string;
	source: string;
	spaceKey?: string;
	onClick: (actionSubjectId: string, attributes?: Record<string, string>) => void;
	flags?: FlagsStateContainer;
	openFrom?: string;
};

export type UseMenuItemReturnProps = MenuItem;

export function useMenuItems(
	flags: FlagsStateContainer,
	closeMenu?: () => void,
	openFrom?: string,
	splitButtonExperimentEnabled?: boolean,
	isMenuOpen?: boolean,
) {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const routeDataRef = useRouteDataRef();
	const isOnEditRoute = useIsEditorPage();
	const isLivePage = useIsCurrentPageLive();
	const { isRovoLLMEnabled, accessStatus, isLoggedIn } = useSessionData();

	const isFreeOrStandardUser = useIsFreeOrStandardUser();

	const handleAnalyticsAndResetStickySearch = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					source: ANALYTICS_EVENT_SOURCE,
					attributes: {
						navdexPointType: END,
						isOnEditRoute,
						currentRouteName: routeDataRef?.current?.routeName,
						currentRouteIsLive: isLivePage,
						openFrom,
						...attributes,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isOnEditRoute, routeDataRef, isLivePage, openFrom],
	);

	const onClick = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			handleAnalyticsAndResetStickySearch(actionSubjectId, attributes);
			closeMenu?.();
		},
		[handleAnalyticsAndResetStickySearch, closeMenu],
	);

	const { canCreateSpace } = useCanCreateSpace();
	const isLoomEntryPointEnabled = useIsCreateMenuLoomRecorderEntryPointEnabled();
	const {
		isCreateWhiteboardEnabled,
		isCreateBlogEnabled,
		isCreateLiveDocEnabled,
		isCreateDatabaseEnabled,
		isCreateSmartLinkEnabled,
	} = useSpaceEnabledContentTypes(spaceKey);

	const { isEnabled: isTemplateTouchpointEnabled } = useCfind5291TemplateTouchpoint({
		skip: !isMenuOpen,
	});

	const startFromTemplate = useStartFromTemplateItem({
		source: 'globalCreateDropdown-startFromTemplate',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const smartCreate = useAISmartCreateItem({
		source: 'globalCreateDropdown-smartCreate',
		spaceKey,
		onClick,
		openFrom,
	});

	const slide = useSlideItem({
		source: 'globalCreateDropdown-slide',
		spaceKey,
		onClick,
	});

	const liveDoc = useLiveDocItem({
		source: 'globalCreateDropdown-liveDoc',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const page = usePageItem({
		source: 'globalCreateDropdown-page',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const whiteboard = useWhiteboardItem({
		source: 'globalCreateDropdown-whiteboard',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const database = useDatabaseItem({
		source: 'globalCreateDropdown-database',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const blog = useBlogItem({
		source: 'globalCreateDropdown-blog',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const embed = useEmbedItem({
		source: 'globalCreateDropdown-smartLinkEmbed',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const loom = useLoomItem({
		source: 'globalCreateDropdown-smartLinkEmbed',
		parentId: contentId,
		spaceKey,
		onClick,
		flags,
	});

	const [createAgent, isAgentItemEligible] = useAgentItem({
		source: 'globalCreateDropdown-createAgent',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const space = useSpaceItem({
		source: ANALYTICS_EVENT_SOURCE,
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isOptedOutedOfWhiteboards = isWhiteboardFeatureEnabled('whiteboardOptOut');
	const isWhiteboardsEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');

	const whiteboardOptIn = !isOptedOutedOfWhiteboards && isWhiteboardsEnabled;

	const databaseOptIn =
		isContentTypeEnabledInCurrentEnv('database') && !fg('confluence_frontend_databases_opt-out');

	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS || !isLoggedIn;

	const isAifcEnabled = isRovoLLMEnabled && !isAnonymousUser && fg('aifc_create_enabled');

	const slideOptIn = isContentTypeEnabledInCurrentEnv('slide');

	const isSlideBaseEnabled = slideOptIn && isRovoLLMEnabled && !isAnonymousUser;

	const getSlideItem = () =>
		isSlideBaseEnabled &&
		expValEquals('confluence_frontend_cwr_outcome_type_picker', 'isEnabled', true) &&
		expValEquals('cc-mui-slides-experiment', 'isEnabled', true)
			? [slide]
			: [];

	const getCommonItems = () => [
		...getSlideItem(),
		...(isCreateWhiteboardEnabled && whiteboardOptIn ? [whiteboard] : []),
		...(isCreateDatabaseEnabled && databaseOptIn ? [database] : []),
		...(isCreateBlogEnabled ? [blog] : []),
		...(isCreateSmartLinkEnabled ? [embed] : []),
	];

	const getRemainingItems = () => {
		return [
			...(isCreateLiveDocEnabled ? [liveDoc] : []),
			page,
			...getCommonItems(),
			...(canCreateSpace ? [space] : []),
			...(isAgentItemEligible &&
			expValEquals('cc_fd_agent_creation_connie_create', 'isEnabled', true)
				? [createAgent]
				: []),
			...(isCreateSmartLinkEnabled && isLoomEntryPointEnabled ? [loom] : []),
		];
	};

	const getNativeItems = () => {
		return [
			...(isAifcEnabled && !splitButtonExperimentEnabled ? [smartCreate] : []),
			...(isCreateLiveDocEnabled ? [liveDoc] : []),
			page,
			...getCommonItems(),
			...(canCreateSpace ? [space] : []),
		];
	};

	const { formatMessage } = useIntl();

	const firstSectionItems = [
		...(isAifcEnabled && !splitButtonExperimentEnabled ? [smartCreate] : []),
		...(isTemplateTouchpointEnabled ? [startFromTemplate] : []),
	];

	const loomAndCreateAgentItems = [
		...(isAgentItemEligible && expValEquals('cc_fd_agent_creation_connie_create', 'isEnabled', true)
			? [createAgent]
			: []),
		...(isCreateSmartLinkEnabled && isLoomEntryPointEnabled ? [loom] : []),
	];

	// Non-free/standard users exit before navx_universal_create_confluence_menu_upgrade evaluates
	// to avoid incorrect experiment exposures. Their behavior matches the experiment's default cohort.
	const cohort = isFreeOrStandardUser
		? expVal('navx_universal_create_confluence_menu_upgrade', 'cohort', 'control')
		: 'not-enrolled';

	switch (cohort) {
		case 'variant_1': {
			return [
				{
					items: firstSectionItems,
				},
				{
					items: getRemainingItems(),
					...(isTemplateTouchpointEnabled
						? { foldable: OVERFLOW_THRESHOLD_TEMPLATE_TOUCHPOINT }
						: {}),
				},
			];
		}

		case 'variant_2': {
			return [
				{
					items: firstSectionItems,
				},
				{
					items: getRemainingItems(),
					foldable: OVERFLOW_THRESHOLD_TEMPLATE_TOUCHPOINT,
					personalize: 'overflow' as const,
				},
			];
		}

		// both not-enrolled and control will see the same experience
		default: {
			// cohort 'default' — existing behavior
			if (isTemplateTouchpointEnabled) {
				return [
					{
						items: firstSectionItems,
					},
					{
						items: getRemainingItems(),
						foldable: OVERFLOW_THRESHOLD_TEMPLATE_TOUCHPOINT,
					},
				];
			}

			return [
				{
					items: getNativeItems(),
				},
				...(loomAndCreateAgentItems.length > 0
					? [
							{
								heading: formatMessage(messages.moreFromAtlassian),
								items: loomAndCreateAgentItems,
							},
						]
					: []),
			];
		}
	}
}
